import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  IconButton,
  Paper,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const slugify = (text) => text.toLowerCase().replace(/\s+/g, "-");

const Search = ({ isMobile }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [productResults, setProductResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setSearchTerm("");
  }, []);

  const handleSearch = () => {
    if (searchTerm.trim().length >= 2) {
      navigate(`/buscar?q=${encodeURIComponent(searchTerm.trim())}`);
      closeDropdown();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSuggestionClick = (item) => {
    if (item.type === "Producto") {
      const sectionSlug = slugify(item.section);
      const categorySlug = slugify(item.category);
      const productSlug = item.slug;
      navigate(`/categorias/${sectionSlug}/${categorySlug}/${productSlug}`);
    } else {
      const typeParam = item.type.toLowerCase();
      navigate(`/buscar?q=${encodeURIComponent(item.name)}&type=${typeParam}`);
    }
    closeDropdown();
  };

  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSuggestions([]);
      setProductResults([]);
      setShowDropdown(false);
      return;
    }
    const controller = new AbortController();
    const fetchSuggestions = async () => {
      try {
        const res = await fetch(
          `/productos/buscar-productos/?query=${encodeURIComponent(searchTerm.trim())}`,
          { signal: controller.signal }
        );
        const data = await res.json();
        const nonProductSuggestions = data.suggestions.filter(
          (s) => s.type !== "Producto"
        );
        setSuggestions(nonProductSuggestions.slice(0, 8));
        setProductResults((data.products || []).slice(0, 5));
        setShowDropdown(true);
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      }
    };
    const delay = setTimeout(fetchSuggestions, 300);
    return () => {
      clearTimeout(delay);
      controller.abort();
    };
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="menu-search-container">
      <TextField
        placeholder="Busca productos, servicios, marcas, modelos"
        variant="standard"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyPress}
        InputProps={{
          endAdornment: (
            <>
              {searchTerm && (
                <IconButton onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              )}
              <IconButton aria-label="Buscar" onClick={handleSearch}>
                <SearchIcon />
              </IconButton>
            </>
          ),
          disableUnderline: true,
          className: "menu-search-input",
        }}
        sx={{
          "& .MuiInputBase-root": { borderBottom: "none" },
          width: isMobile ? "90%" : "100%",
        }}
      />
      {showDropdown && (
        <Paper
          ref={dropdownRef}
          sx={{
            mt: 1,
            p: 2,
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: isMobile ? "90%" : 700,
            zIndex: 10,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Sugerencias
              </Typography>
              {suggestions.length > 0 ? (
                suggestions.map((item, index) => (
                  <Box
                    key={index}
                    onClick={() => handleSuggestionClick(item)}
                    sx={{ cursor: "pointer", mb: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {item.type}
                    </Typography>
                    <Typography variant="body2">{item.name}</Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hay sugerencias disponibles de secciones o categorías
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" gutterBottom>
                Productos
              </Typography>
              <Grid container spacing={1}>
                {productResults.map((product) => (
                  <Grid
                    item
                    xs={12}
                    key={product.id}
                    onClick={() =>
                      handleSuggestionClick({ ...product, type: "Producto" })
                    }
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={product.images?.[0]}
                      alt={product.name}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: "contain",
                        borderRadius: 1,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontWeight: "normal" }}
                    >
                      {product.name}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
    </div>
  );
};

export default Search;
